// Resolve the API base URL + install a global axios interceptor.
//
// Why: the deployed `kinsy.io` bundle hard-codes the API URL to
// `family-connect-195.emergent.host`, which is a *different eTLD+1* from
// `kinsy.io`. Safari ITP and Chrome's third-party cookie blocking strip
// the auth cookie on those cross-site requests, breaking login/post/etc.
//
// Fix: when running on kinsy.io / kinsy.app, rewrite every outbound request
// targeting the env-configured backend host to the *current origin* instead.
// kinsy.io's ingress already proxies `/api/*` to the backend, so this just
// keeps the cookie first-party.
import axios from 'axios';

function resolveApiBase() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  if (typeof window === 'undefined') return envUrl + '/api';

  const host = window.location.hostname || '';
  if (/(?:^|\.)kinsy\.(io|app)$/i.test(host)) {
    return window.location.origin + '/api';
  }
  return envUrl + '/api';
}

export const API_BASE = resolveApiBase();

function shouldRewrite(reqUrl) {
  if (!reqUrl) return false;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname || '';
  if (!/(?:^|\.)kinsy\.(io|app)$/i.test(host)) return false;
  // Only rewrite absolute URLs that include http(s)
  if (!/^https?:\/\//i.test(reqUrl)) return false;
  try {
    const u = new URL(reqUrl);
    if (u.hostname === window.location.hostname) return false;
    // Rewrite anything pointing at the env backend host (or its parent domain)
    return /\/api(\/|$|\?)/.test(u.pathname);
  } catch (_) {
    return false;
  }
}

let _installed = false;
let _refreshInFlight = null;

async function _refreshToken() {
  // Use raw axios call (no recursion through this interceptor's pre-flight pass).
  // Note: response interceptor still applies, but we mark this request with
  // `_isRefresh` so we don't try to re-refresh inside the refresh.
  return axios.post(`${API_BASE}/auth/refresh`, null, {
    withCredentials: true,
    _isRefresh: true,
  });
}

export function installApiInterceptor() {
  if (_installed) return;
  _installed = true;
  axios.interceptors.request.use((config) => {
    const target = config.url || '';
    if (shouldRewrite(target)) {
      try {
        const u = new URL(target);
        config.url = window.location.origin + u.pathname + u.search + u.hash;
      } catch (_) {
        /* leave unchanged */
      }
    }
    // Always send credentials for cookie-based auth
    if (config.withCredentials !== false) config.withCredentials = true;
    return config;
  });

  // Auto-refresh on 401: hits /api/auth/refresh once, then replays the original
  // request. If refresh fails too → bubble the original 401 (user needs to re-login).
  axios.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      const original = error?.config || {};
      const status = error?.response?.status;
      // Skip if: not 401, already retried, this IS the refresh call, or no original config
      if (status !== 401 || original._retried || original._isRefresh) {
        return Promise.reject(error);
      }
      // Don't try to refresh on the login / register / refresh endpoints themselves
      const url = String(original.url || '');
      if (/\/auth\/(login|register|refresh|forgot-password|reset-password)/.test(url)) {
        return Promise.reject(error);
      }
      original._retried = true;
      try {
        if (!_refreshInFlight) {
          _refreshInFlight = _refreshToken().finally(() => { _refreshInFlight = null; });
        }
        await _refreshInFlight;
        // Replay the original request with the refreshed cookie
        return axios(original);
      } catch (_e) {
        return Promise.reject(error);
      }
    }
  );
}

// Auto-install on import.
installApiInterceptor();
